import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Layers, ShieldCheck, Tags } from 'lucide-react';
import type { PayerPlanDto, PayerServiceCodeDto, PreAuthRuleDto } from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { ConfirmDialog, DataTable, Tabs } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Badge, Button, Checkbox, DatePicker, Field, FormSkeleton, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';
import type { Payer } from './PayerFormPage';

const TAB_IDS = ['plans', 'rules', 'service-codes'];

export function PayerManagePage() {
  const { payerId } = useParams<{ payerId: string }>();
  const [params, setParams] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('payers'));
  const requested = params.get('tab');
  const tab = requested && TAB_IDS.includes(requested) ? requested : 'plans';
  const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({});
  const payers = useQuery({
    queryKey: ['payers', 'all'],
    queryFn: () => get<Payer[]>('/insurance/payers?includeInactive=true'),
  });
  const payer = payers.data?.find((row) => row.id === payerId) ?? null;

  if (payers.isLoading) return <FormSkeleton />;
  if (!payer || !payerId) return <Alert variant="destructive">{payers.error ? describeError(payers.error) : 'This payer is not on file.'}</Alert>;

  const markDirty = (id: string) => (dirty: boolean) => setDirtyTabs((current) => (current[id] === dirty ? current : { ...current, [id]: dirty }));

  return (
    <FormScreen
      title={payer.name}
      description={tr('billing', 'plans_rules_and_service_codes_of_this_payer')}
      icon={Building2}
      eyebrow={tr('billing', 'payer')}
      backTo={returnTo}
      dirty={Object.values(dirtyTabs).some(Boolean)}
      wide
    >
      <Tabs
        active={tab}
        onChange={(id) => {
          params.set('tab', id);
          setParams(params, { replace: true });
        }}
        help={false}
        tabs={[
          { id: 'plans', label: tr('billing', 'plans') },
          { id: 'rules', label: tr('billing', 'pre_authorisation_rules') },
          { id: 'service-codes', label: tr('billing', 'service_mapping') },
        ]}
      />
      {tab === 'plans' && <PlansTab payerId={payerId} onDirtyChange={markDirty('plans')} />}
      {tab === 'rules' && <RulesTab payer={payer} onDirtyChange={markDirty('rules')} />}
      {tab === 'service-codes' && <ServiceCodesTab payerId={payerId} onDirtyChange={markDirty('service-codes')} />}
    </FormScreen>
  );
}

interface PlanForm {
  code: string;
  name: string;
  patientSharePct: string;
  fixedPerVisit: string;
  maxPerVisit: string;
  maxPerDay: string;
  maxPerYear: string;
  annualDeductible: string;
  classValue: string;
  network: string;
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

const EMPTY_PLAN: PlanForm = {
  code: '',
  name: '',
  patientSharePct: '',
  fixedPerVisit: '',
  maxPerVisit: '',
  maxPerDay: '',
  maxPerYear: '',
  annualDeductible: '',
  classValue: '',
  network: '',
  validFrom: '',
  validTo: '',
  isActive: true,
};

const toMajor = (minor: number | null) => (minor === null ? '' : formatMoneyInput(minor));
const toMinor = (value: string) => (value.trim() === '' ? null : minorFromInput(value));

function planFormOf(plan: PayerPlanDto | null): PlanForm {
  if (!plan) return EMPTY_PLAN;
  return {
    code: plan.code,
    name: plan.name,
    patientSharePct: plan.patientSharePct === null ? '' : String(plan.patientSharePct),
    fixedPerVisit: toMajor(plan.fixedPerVisitMinor),
    maxPerVisit: toMajor(plan.maxPerVisitMinor),
    maxPerDay: toMajor(plan.maxPerDayMinor),
    maxPerYear: toMajor(plan.maxPerYearMinor),
    annualDeductible: toMajor(plan.annualDeductibleMinor),
    classValue: plan.classValue ?? '',
    network: plan.network ?? '',
    validFrom: plan.validFrom ?? '',
    validTo: plan.validTo ?? '',
    isActive: plan.isActive,
  };
}

function PlansTab({ payerId, onDirtyChange }: { payerId: string; onDirtyChange: (dirty: boolean) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PayerPlanDto | null>(null);
  const [form, setForm] = useState<PlanForm>(EMPTY_PLAN);
  const [moreRules, setMoreRules] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plans = useQuery({
    queryKey: ['payer-plans', payerId],
    queryFn: () => get<PayerPlanDto[]>(`/insurance/payers/${payerId}/plans`),
  });

  const open = (plan: PayerPlanDto | null) => {
    setEditing(plan);
    const next = planFormOf(plan);
    setForm(next);
    setMoreRules(Boolean(next.maxPerVisit || next.maxPerDay || next.maxPerYear || next.classValue || next.network));
    setError(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code,
        name: form.name,
        patientSharePct: form.patientSharePct.trim() === '' ? null : Number(form.patientSharePct),
        fixedPerVisitMinor: toMinor(form.fixedPerVisit),
        maxPerVisitMinor: toMinor(form.maxPerVisit),
        maxPerDayMinor: toMinor(form.maxPerDay),
        maxPerYearMinor: toMinor(form.maxPerYear),
        annualDeductibleMinor: toMinor(form.annualDeductible),
        classValue: form.classValue.trim() || null,
        network: form.network.trim() || null,
        validFrom: form.validFrom || null,
        validTo: form.validTo || null,
        isActive: form.isActive,
      };
      return editing ? put<PayerPlanDto>(`/insurance/payers/${payerId}/plans/${editing.id}`, body) : post<PayerPlanDto>(`/insurance/payers/${payerId}/plans`, body);
    },
    onSuccess: () => {
      toast.success('Plan saved', 'Bills already issued keep the split they were issued with');
      void queryClient.invalidateQueries({ queryKey: ['payer-plans', payerId] });
      open(null);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(planFormOf(editing));
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const amount = (minor: number | null) => (minor === null ? '—' : money(minor));

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">What the patient pays under each plan. A plan with nothing set here falls back to the policy’s or the payer’s co-pay.</p>
      <DataTable
        dense
        rows={plans.data}
        loading={plans.isLoading} refreshing={plans.isFetching}
        error={plans.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => open(row)}
        empty="No plans yet. Every patient of this payer pays the payer’s co-pay."
        columns={[
          { header: 'Code', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
          { header: 'Plan', sort: (row) => row.name, cell: (row) => row.name },
          { header: 'Patient pays', align: 'right', sort: (row) => row.patientSharePct, cell: (row) => (row.patientSharePct === null ? '—' : `${row.patientSharePct}%`) },
          { header: 'Per visit', align: 'right', sort: (row) => row.fixedPerVisitMinor, cell: (row) => amount(row.fixedPerVisitMinor) },
          { header: 'Paid first each year', align: 'right', sort: (row) => row.annualDeductibleMinor, cell: (row) => amount(row.annualDeductibleMinor) },
          { header: 'Valid', sort: (row) => row.validFrom, cell: (row) => (row.validFrom || row.validTo ? `${row.validFrom ?? '…'} – ${row.validTo ?? '…'}` : 'Always') },
          { header: '', cell: (row) => (row.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Off</Badge>) },
        ]}
      />

      <form
        noValidate
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (form.code.trim() && form.name.trim().length >= 2) save.mutate();
        }}
      >
        <FormSection title={editing ? editing.name : 'Add a plan'} icon={Layers}>
          <Field label="Code" required>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
        </FormSection>
        <FormSection title={tr('billing', 'what_the_patient_pays')}>
          <Field label="Percentage the patient pays" hint="Of each service, after the yearly amount below">
            <Input type="number" min={0} max={100} step="0.01" value={form.patientSharePct} onChange={(e) => setForm({ ...form, patientSharePct: e.target.value })} />
          </Field>
          <Field label="Fixed amount per visit" hint="Taken once per visit or admission, on top of the percentage">
            <Input type="text" dir="ltr" inputMode="decimal" value={form.fixedPerVisit} onChange={(e) => setForm({ ...form, fixedPerVisit: e.target.value })} />
          </Field>
          <Field label="Yearly amount the patient pays first" hint="Before the insurer pays anything that year">
            <Input type="text" dir="ltr" inputMode="decimal" value={form.annualDeductible} onChange={(e) => setForm({ ...form, annualDeductible: e.target.value })} />
          </Field>
          <div className="self-end sm:col-span-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setMoreRules(!moreRules)}>
              {moreRules ? 'Fewer rules' : 'More rules'}
            </Button>
          </div>
          {moreRules && (
            <>
              <Field label="Most the patient pays per visit">
                <Input type="text" dir="ltr" inputMode="decimal" value={form.maxPerVisit} onChange={(e) => setForm({ ...form, maxPerVisit: e.target.value })} />
              </Field>
              <Field label="Most the patient pays per day">
                <Input type="text" dir="ltr" inputMode="decimal" value={form.maxPerDay} onChange={(e) => setForm({ ...form, maxPerDay: e.target.value })} />
              </Field>
              <Field label="Most the patient pays per year">
                <Input type="text" dir="ltr" inputMode="decimal" value={form.maxPerYear} onChange={(e) => setForm({ ...form, maxPerYear: e.target.value })} />
              </Field>
              <Field label="Class on the insurer’s policy" hint="Matches this plan to a policy the insurer returns">
                <Input value={form.classValue} onChange={(e) => setForm({ ...form, classValue: e.target.value })} />
              </Field>
              <Field label="Network">
                <Input value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} />
              </Field>
            </>
          )}
        </FormSection>
        <FormSection title={tr('billing', 'validity')}>
          <Field label="Valid from">
            <DatePicker value={form.validFrom} onChange={(value) => setForm({ ...form, validFrom: value })} />
          </Field>
          <Field label="Valid until">
            <DatePicker value={form.validTo} onChange={(value) => setForm({ ...form, validTo: value })} />
          </Field>
          <Checkbox className="self-end" checked={form.isActive} onCheckedChange={(checked) => setForm({ ...form, isActive: checked })} label="In use" />
          {error && (
            <div className="sm:col-span-2">
              <Alert variant="destructive">{error}</Alert>
            </div>
          )}
        </FormSection>
        <FormActions
          submitLabel="Save plan"
          cancelLabel={editing ? 'Back to the list' : undefined}
          loading={save.isPending}
          incomplete={!form.code.trim() || form.name.trim().length < 2}
          onCancel={() => open(null)}
        />
      </form>
    </div>
  );
}

const CATEGORIES = ['consultation', 'procedure', 'lab', 'radiology', 'surgery', 'room', 'nursing', 'other'];

const EMPTY_RULE = { planId: '', chargeCategory: '', minAmount: '', afterCount: '', countPeriod: '' };

function RulesTab({ payer, onDirtyChange }: { payer: Payer; onDirtyChange: (dirty: boolean) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_RULE);
  const [error, setError] = useState<string | null>(null);
  const rules = useQuery({ queryKey: ['preauth-rules', payer.id], queryFn: () => get<PreAuthRuleDto[]>(`/insurance/payers/${payer.id}/preauth-rules`) });
  const plans = useQuery({ queryKey: ['payer-plans', payer.id], queryFn: () => get<PayerPlanDto[]>(`/insurance/payers/${payer.id}/plans`), retry: false });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['preauth-rules', payer.id] });
  const add = useMutation({
    mutationFn: () =>
      post(`/insurance/payers/${payer.id}/preauth-rules`, {
        planId: form.planId || null,
        chargeCategory: form.chargeCategory || null,
        minAmountMinor: form.minAmount.trim() === '' ? null : minorFromInput(form.minAmount),
        afterCount: form.afterCount.trim() === '' ? null : Number(form.afterCount),
        countPeriod: form.countPeriod || null,
      }),
    onSuccess: () => {
      toast.success('Rule added');
      setForm(EMPTY_RULE);
      setError(null);
      refresh();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/insurance/payers/${payer.id}/preauth-rules/${id}`),
    onSuccess: () => refresh(),
    onError: (caught) => toast.error(tr('billing', 'could_not_switch_the_rule_off'), describeError(caught)),
  });
  const [stopping, setStopping] = useState<string | null>(null);

  const dirty = JSON.stringify(form) !== JSON.stringify(EMPTY_RULE);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        {payer.requiresPreAuth
          ? 'With no rule, everything this payer covers needs a pre-authorisation. Add rules to ask only for what the contract names.'
          : 'With no rule, nothing needs a pre-authorisation. Add a rule for each kind of service the contract names.'}
      </p>
      <DataTable
        dense
        rows={rules.data}
        loading={rules.isLoading} refreshing={rules.isFetching}
        error={rules.error}
        rowKey={(row) => row.id}
        empty="No rules."
        columns={[
          { header: 'Plan', sort: (row) => row.planName, cell: (row) => row.planName ?? 'Every plan' },
          { header: 'Service', sort: (row) => row.chargeItemName ?? row.chargeCategory ?? row.benefitCategory, cell: (row) => row.chargeItemName ?? (row.chargeCategory || row.benefitCategory ? humanize(row.chargeCategory ?? row.benefitCategory) : 'Any') },
          { header: 'From', align: 'right', sort: (row) => row.minAmountMinor, cell: (row) => (row.minAmountMinor === null ? '—' : money(row.minAmountMinor)) },
          { header: 'After', sort: (row) => row.afterCount, cell: (row) => (row.afterCount === null ? '—' : `${row.afterCount} per ${row.countPeriod}`) },
          { header: '', cell: (row) => (row.isActive ? null : <Badge variant="muted">Off</Badge>) },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.isActive && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setStopping(row.id)}>
                  Stop using
                </Button>
              ),
          },
        ]}
      />
      {stopping && (
        <ConfirmDialog
          consequence="stop"
          destructive
          onConfirm={() => remove.mutateAsync(stopping)}
          onClose={() => setStopping(null)}
        />
      )}
      <form
        noValidate
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <FormSection title={tr('billing', 'new_rule')} icon={ShieldCheck} columns={3}>
          <Field label="Plan">
            <Select value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })}>
              <option value="">Every plan</option>
              {plans.data?.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kind of service">
            <Select value={form.chargeCategory} onChange={(e) => setForm({ ...form, chargeCategory: e.target.value })}>
              <option value="">Any</option>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {humanize(category, 'charge_category')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From amount">
            <Input type="text" dir="ltr" inputMode="decimal" value={form.minAmount} onChange={(e) => setForm({ ...form, minAmount: e.target.value })} />
          </Field>
          <Field label="After (times)">
            <Input type="number" min={0} value={form.afterCount} onChange={(e) => setForm({ ...form, afterCount: e.target.value })} />
          </Field>
          <Field label="Per">
            <ChoiceButtons
              value={form.countPeriod}
              onChange={(countPeriod) => setForm({ ...form, countPeriod })}
              onClear={() => setForm({ ...form, countPeriod: '' })}
              options={[
                { value: 'visit', label: humanize('visit', 'count_period') },
                { value: 'year', label: humanize('year', 'count_period') },
              ]}
            />
          </Field>
          {error && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Alert variant="destructive">{error}</Alert>
            </div>
          )}
        </FormSection>
        <FormActions submitLabel="Add rule" loading={add.isPending} onCancel={() => setForm(EMPTY_RULE)} />
      </form>
    </div>
  );
}

function ServiceCodesTab({ payerId, onDirtyChange }: { payerId: string; onDirtyChange: (dirty: boolean) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [chargeItemId, setChargeItemId] = useState('');
  const [code, setCode] = useState('');
  const [system, setSystem] = useState('');
  const [display, setDisplay] = useState('');
  const [nonStandard, setNonStandard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState<{ id: string; code: string } | null>(null);
  const list = useQuery({
    queryKey: ['payer-service-codes', payerId],
    queryFn: () => get<{ items: PayerServiceCodeDto[] }>(`/insurance/payers/${payerId}/service-codes${qs({ limit: 200 })}`),
  });
  const charges = useQuery({
    queryKey: ['charge-items', 'for-service-codes', search],
    queryFn: () => get<{ data: { id: string; code: string; name: string }[] }>(`/charge-items${qs({ q: search || undefined, packages: 1, limit: 30 })}`),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['payer-service-codes', payerId] });
  const reset = () => {
    setChargeItemId('');
    setCode('');
    setSystem('');
    setDisplay('');
    setNonStandard(false);
  };
  const save = useMutation({
    mutationFn: () =>
      put(`/insurance/payers/${payerId}/service-codes`, {
        chargeItemId,
        code: code.trim(),
        system: system.trim() || null,
        display: display.trim() || null,
        isNonStandard: nonStandard,
      }),
    onSuccess: () => {
      toast.success('Code saved');
      setChargeItemId('');
      setCode('');
      setDisplay('');
      setNonStandard(false);
      setError(null);
      refresh();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/insurance/payers/${payerId}/service-codes/${id}`),
    onSuccess: () => refresh(),
    onError: (caught) => toast.error('Could not remove it', describeError(caught)),
  });

  const dirty = Boolean(chargeItemId || code || system || display || nonStandard);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">This payer’s own code for a service, used ahead of the facility-wide code map</p>
      <DataTable
        dense
        rows={list.data?.items}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        empty="No payer-specific codes; the facility-wide code map is used."
        columns={[
          { header: 'Service', sort: (row) => row.localCode ?? row.localName, cell: (row) => `${row.localCode ?? ''} ${row.localName ?? ''}` },
          { header: 'Payer’s code', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
          { header: 'System', sort: (row) => row.system, cell: (row) => <span className="text-xs text-muted-foreground">{row.system ?? 'Default'}</span> },
          { header: '', cell: (row) => (row.isNonStandard ? <Badge variant="warning">Non-standard</Badge> : null) },
          { header: '', cell: (row) => (row.isActive ? null : <Badge variant="muted">Off</Badge>) },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.isActive && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setStopping({ id: row.id, code: row.code })}>
                  Stop using
                </Button>
              ),
          },
        ]}
      />
      {stopping && (
        <ConfirmDialog
          consequence="stop"
          destructive
          subject={stopping.code}
          onConfirm={() => remove.mutateAsync(stopping.id)}
          onClose={() => setStopping(null)}
        />
      )}
      <form
        noValidate
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (chargeItemId && code.trim()) save.mutate();
        }}
      >
        <FormSection title={tr('billing', 'map_a_service')} icon={Tags}>
          <Field label="Search services">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} />
          </Field>
          <Field label="Service" required>
            <Select value={chargeItemId} onChange={(e) => setChargeItemId(e.target.value)}>
              <option value="">Choose a service…</option>
              {charges.data?.data.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.code} — {row.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Payer’s code" required>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label="Code system (optional)">
            <Input value={system} onChange={(e) => setSystem(e.target.value)} />
          </Field>
          <Field label="Payer’s name for it">
            <Input value={display} onChange={(e) => setDisplay(e.target.value)} />
          </Field>
          <Checkbox className="self-end" checked={nonStandard} onCheckedChange={setNonStandard} label="Not a standard code" />
          {error && (
            <div className="sm:col-span-2">
              <Alert variant="destructive">{error}</Alert>
            </div>
          )}
        </FormSection>
        <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!chargeItemId || !code.trim()} onCancel={reset} />
      </form>
    </div>
  );
}
